import React, { useState, useEffect } from 'react';
import { Card, Form, Row, Col, Button, Alert } from 'react-bootstrap';
import programService from '@/shared/services/programService';

const describeList = (programLabel, prefixes) =>
    prefixes.length > 0
        ? `${programLabel} serves ${prefixes.join(', ')} courses.`
        : `${programLabel} serves courses with any prefix.`;

/**
 * A program's own list of the course prefixes it serves, edited by its
 * coordinator. The text goes to the server as typed: the server normalises
 * it, refuses a malformed prefix with a message shown here, and returns the
 * stored list, which replaces what was typed.
 */
function ProgramCoursePrefixes({ programType, programLabel }) {
    // null until loaded, so an unknown list is never saved over a real one.
    const [savedPrefixes, setSavedPrefixes] = useState(null);
    const [text, setText] = useState('');
    const [loadError, setLoadError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [status, setStatus] = useState('');

    useEffect(() => {
        let active = true;
        const show = (prefixes) => {
            setSavedPrefixes(prefixes);
            setText(prefixes.join(', '));
        };
        programService
            .getConfig(programType)
            .then((config) => {
                if (active) show(config?.allowedSubjectPrefixes || []);
            })
            .catch((err) => {
                if (!active) return;
                // A program nobody has configured yet serves every prefix.
                if (err.response?.status === 404) {
                    show([]);
                } else {
                    setLoadError(
                        'Could not load the course prefixes for this program.'
                    );
                }
            });
        return () => {
            active = false;
        };
    }, [programType]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setSaveError(null);
        setStatus('');
        try {
            const config = await programService.upsertConfig(programType, {
                allowedSubjectPrefixes: text,
            });
            const prefixes = config.allowedSubjectPrefixes;
            setSavedPrefixes(prefixes);
            setText(prefixes.join(', '));
            setStatus(`Saved. ${describeList(programLabel, prefixes)}`);
        } catch (err) {
            setSaveError(
                err.response?.data?.message || 'Failed to save course prefixes.'
            );
        } finally {
            setSaving(false);
        }
    };

    const inputId = `${programType}-course-prefixes`;
    const helpId = `${inputId}-help`;
    const errorId = `${inputId}-error`;
    const unavailable = savedPrefixes === null;

    return (
        <Card className="border-0 shadow-sm mb-4">
            <Card.Body className="py-3">
                <h2 className="h6 mb-3">Courses Served</h2>
                {loadError && (
                    <Alert variant="warning" className="small py-2">
                        {loadError}
                    </Alert>
                )}
                <Form onSubmit={handleSubmit}>
                    <Row className="align-items-end">
                        <Col md={10}>
                            <Form.Label htmlFor={inputId}>
                                Course prefixes {programLabel} serves
                            </Form.Label>
                            <Form.Control
                                id={inputId}
                                type="text"
                                placeholder="Every prefix"
                                value={text}
                                disabled={unavailable}
                                aria-invalid={saveError ? true : undefined}
                                aria-describedby={
                                    saveError ? `${helpId} ${errorId}` : helpId
                                }
                                onChange={(e) => setText(e.target.value)}
                            />
                        </Col>
                        <Col md={2} className="mt-2 mt-md-0">
                            <Button
                                type="submit"
                                variant="primary"
                                className="w-100"
                                disabled={unavailable || saving}
                            >
                                {saving ? 'Saving...' : 'Save prefixes'}
                            </Button>
                        </Col>
                    </Row>
                    <Form.Text id={helpId}>
                        Comma or space separated, like CHE, MTH. Leave empty to
                        serve courses with any prefix. Faculty see{' '}
                        {programLabel} as unavailable for a course outside this
                        list.
                    </Form.Text>
                    {saveError && (
                        <div
                            id={errorId}
                            className="text-danger small mt-1"
                            role="alert"
                        >
                            {saveError}
                        </div>
                    )}
                    <div className="small text-muted mt-1" role="status">
                        {status}
                    </div>
                </Form>
            </Card.Body>
        </Card>
    );
}

export default ProgramCoursePrefixes;
